// Shared design tokens of the connected webapp (dark, same colors as the SoSkills app).
// Every style.ts imports them so the spacing and the sizes are the same on every screen.

import { SnackbarOrigin } from '@mui/material';
import { keyframes } from '@mui/system';

const colors = {
  background: '#000000',
  surface: '#121212',
  surfaceRaised: '#1A1A1A',
  surfaceHover: '#222222',
  border: '#1C2833',
  borderSoft: '#1A1A1A',
  borderHover: '#2C3E50',
  // Dark buttons: lighter than the cards' border, so their edges show next to a white button.
  buttonBorder: '#2C3E50',
  borderFocus: '#4A5B6D',
  scrollbar: '#2A3542',
  chipBackground: '#1F1F1F',
  text: '#FFFFFF',
  textLight: '#E5E5E5',
  textSecondary: '#A9A7A7',
  textSubtle: '#8A8A8A',
  danger: '#F44336',
  dangerSoft: 'rgba(244, 67, 54, 0.16)',
  dangerText: '#FF8A80',
  warning: '#FAAD14',
  warningSoft: 'rgba(250, 173, 20, 0.16)',
  warningBorder: 'rgba(250, 173, 20, 0.35)',
  warningText: '#FFE7BA',
  success: '#52C41A',
  successSoft: 'rgba(82, 196, 26, 0.12)',
  successText: '#95DE64',
  successBorder: 'rgba(82, 196, 26, 0.3)',
  top: '#FFD700',
  topSoft: 'rgba(255, 215, 0, 0.14)',
  // Admin notifications: unread, and the messages sent by the admin.
  info: '#7DD3FC',
  infoSoft: 'rgba(125, 211, 252, 0.14)',
  // Podium of the weekly ranking (gold = top), the other ranks in grey.
  topEnd: '#FF8A00',
  rankSilver: '#C9CED3',
  rankBronze: '#D08A5A',
  rankOther: '#6B6B6B',
  storyRing: '#2A2A2A',
  overlay: 'rgba(0, 0, 0, 0.6)',
  activeItem: 'rgba(255, 255, 255, 0.08)',
  // Outline of the active item of the side menu: the cards' border doesn't show on the menu.
  activeBorder: '#34404B',
  activeIcon: 'rgba(255, 255, 255, 0.12)',
  hoverItem: 'rgba(255, 255, 255, 0.06)',
  rowHover: 'rgba(255, 255, 255, 0.03)',
  // @username in a comment and in the suggestions of a mention.
  mention: '#8FB0FF',
};

const fonts = {
  text: 'Poppins',
  title: 'Anton',
  // On trial for the logo ("SoSkills" / "Admin"), the titles of the pages and of the auth pages.
  // To go back: the logo of Layout (26px) and of Nav and authTitle (weight 400) on `title`,
  // pageTitle on `text` (24px).
  display: 'Rajdhani',
};

// Heights in px.
const sizes = {
  // Top bar of the phone (the admin dashboard), and the header of the drawers and of the upload popup.
  topBar: 56,
  sideNav: 248,
  // Same space around the side menu, between it and the content, and around the content.
  gutter: 16,
  button: 36,
  buttonBlock: 44,
  input: 40,
  // Filters (selects and searches of the filter bars): 15% higher, the label and the value of a select have room.
  filter: 46,
  cardHeader: 52,
  drawer: 480,
};

const radius = {
  button: '8px',
  input: '10px',
  card: '14px',
  chip: '6px',
};

const button = {
  height: sizes.button,
  minWidth: 0,
  px: 1.75,
  borderRadius: radius.button,
  border: `1px solid ${colors.buttonBorder}`,
  backgroundColor: colors.surfaceRaised,
  color: colors.text,
  fontFamily: fonts.text,
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  // Same height for every button (MUI Button, LoadingButton): the border is inside the 36px.
  boxSizing: 'border-box',
  lineHeight: 1,
  '&:hover': {
    backgroundColor: colors.surfaceHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: colors.textSubtle,
    opacity: 0.5,
  },
};

// The border is transparent and the white stops inside it: the dark buttons show their border
// in the color of the page, so both look as big.
const buttonPrimary = {
  ...button,
  border: '1px solid transparent',
  backgroundClip: 'padding-box',
  backgroundColor: colors.text,
  color: colors.background,
  '&:hover': {
    backgroundColor: colors.textLight,
    boxShadow: 'none',
  },
  // Disabled: same white button, faded (the text stays readable).
  '&.Mui-disabled': {
    backgroundColor: colors.text,
    color: colors.background,
    opacity: 0.4,
  },
};

// Same box as the other buttons: the border is transparent, the red fills the inside only
// (a red border made it look bigger next to the dark buttons).
const buttonDanger = {
  ...button,
  border: '1px solid transparent',
  backgroundClip: 'padding-box',
  backgroundColor: colors.danger,
  '&:hover': {
    backgroundColor: '#E53935',
    boxShadow: 'none',
  },
};

const buttonIcon = {
  width: sizes.button,
  height: sizes.button,
  borderRadius: radius.button,
  color: colors.text,
  '&:hover': {
    backgroundColor: colors.activeItem,
  },
};

const buttonBlock = {
  height: sizes.buttonBlock,
  fontSize: 14,
  width: '100%',
};

const input = {
  height: sizes.input,
  px: 1.5,
  borderRadius: radius.input,
  border: `1px solid ${colors.border}`,
  backgroundColor: colors.background,
  color: colors.text,
  fontFamily: fonts.text,
  fontSize: 13,
  boxSizing: 'border-box',
  // The default padding of the MUI input is not symmetric: the text is centered with none.
  '& .MuiInputBase-input': {
    py: 0,
    height: '100%',
  },
};

// Field of a filter bar: same look as an input, a bit higher.
const filterInput = {
  ...input,
  height: sizes.filter,
};

const appear = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;

// Content from the server, played once when it shows up (e.g. the table of a page).
const fadeIn = {
  animation: `${appear} 140ms ease-out backwards`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

// Its overflow is hidden: in a column of fixed height, without flexShrink 0 a card is squeezed
// and its content cut (avatar, username…). The page scrolls instead.
const card = {
  flexShrink: 0,
  backgroundColor: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.card,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

// Text tab (profile, ranking panel): grey, white with an underline when active.
const tab = {
  height: 44,
  px: 3,
  border: 0,
  backgroundColor: 'transparent',
  boxShadow: 'inset 0 -2px 0 transparent',
  color: colors.textSubtle,
  fontFamily: fonts.text,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  // No focus ring after a click, only for the keyboard.
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: `2px solid ${colors.borderFocus}`,
    outlineOffset: -2,
    borderRadius: '4px',
  },
};

const tabActive = {
  ...tab,
  color: colors.text,
  boxShadow: `inset 0 -2px 0 ${colors.text}`,
};

const chip = {
  fontFamily: fonts.text,
  fontSize: 11.5,
  fontWeight: 500,
  color: colors.textLight,
  backgroundColor: colors.chipBackground,
  px: 1,
  py: 0.375,
  borderRadius: radius.chip,
  whiteSpace: 'nowrap',
};

const badge = {
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: fonts.text,
  fontSize: 11,
  fontWeight: 700,
  px: 1,
  py: 0.375,
  borderRadius: radius.chip,
  whiteSpace: 'nowrap',
};

const badges = {
  top: { ...badge, backgroundColor: colors.top, color: '#1A1500' },
  warning: { ...badge, backgroundColor: colors.warningSoft, color: colors.warning },
  danger: { ...badge, backgroundColor: colors.dangerSoft, color: colors.dangerText },
  success: { ...badge, backgroundColor: colors.successSoft, color: colors.successText },
  muted: { ...badge, backgroundColor: colors.chipBackground, color: colors.textSecondary },
  info: { ...badge, backgroundColor: colors.infoSoft, color: colors.info },
  topSoft: { ...badge, backgroundColor: colors.topSoft, color: colors.top },
  count: { ...badge, backgroundColor: colors.danger, color: colors.text, borderRadius: '999px' },
};

// Small uppercase label above a value (filters, tiles, table heads).
const label = {
  fontFamily: fonts.text,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: colors.textSubtle,
  textTransform: 'uppercase',
};

const pageTitle = {
  fontFamily: fonts.display,
  fontSize: 28,
  fontWeight: 700,
  lineHeight: '32px',
  color: colors.text,
};

const notice = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.25,
  p: 1.5,
  borderRadius: radius.input,
  fontFamily: fonts.text,
  fontSize: 12.5,
  lineHeight: 1.5,
};

const notices = {
  warning: {
    ...notice,
    backgroundColor: 'rgba(250, 173, 20, 0.08)',
    border: `1px solid ${colors.warningBorder}`,
    color: colors.warningText,
  },
  success: {
    ...notice,
    backgroundColor: 'rgba(82, 196, 26, 0.08)',
    border: `1px solid ${colors.successBorder}`,
    color: '#D9F7BE',
  },
};

// Key / value block (video details, user stats).
const tile = {
  backgroundColor: colors.surfaceRaised,
  borderRadius: radius.button,
  px: 1.5,
  py: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  minWidth: 0,
};

const tileKey = {
  fontFamily: fonts.text,
  fontSize: 11,
  color: colors.textSubtle,
};

const tileValue = {
  fontFamily: fonts.text,
  fontSize: 13,
  fontWeight: 600,
  color: colors.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Clickable @username in a text.
const link = {
  border: 0,
  background: 'transparent',
  p: 0,
  cursor: 'pointer',
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'underline',
  textDecorationColor: colors.borderFocus,
  textUnderlineOffset: '3px',
  '&:hover': {
    textDecorationColor: colors.text,
  },
};

// On a computer the content area is the window minus the gutter above and below it.
const mainHeight = `calc(100vh - ${2 * sizes.gutter}px)`;

// Items of the side menu: a link, or a button that does the same thing (publish, notifications, log out).
// The active one has no background of its own: a highlight slides behind the items (navIndicator).
const navItem = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  height: 44,
  px: 1.25,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: '12px',
  border: '1px solid transparent',
  backgroundColor: 'transparent',
  color: colors.textSecondary,
  textDecoration: 'none',
  textAlign: 'left',
  fontFamily: fonts.text,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colors.hoverItem,
    color: colors.text,
  },
};

const navItemActive = {
  ...navItem,
  color: colors.text,
  fontWeight: 700,
  '&:hover': {
    color: colors.text,
  },
};

// Height of an item plus the gap under it: the highlight moves by that step.
const navItemStep = 48;

// The highlight of the active page, behind the items: it slides from one to the next on a click.
const navIndicator = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  height: 44,
  boxSizing: 'border-box',
  borderRadius: '12px',
  border: `1px solid ${colors.activeBorder}`,
  backgroundColor: colors.activeItem,
  pointerEvents: 'none',
  transition: 'transform 300ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 150ms',
};

const navIcon = {
  width: 30,
  height: 30,
  borderRadius: radius.input,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    fontSize: 18,
  },
};

const navIconActive = {
  ...navIcon,
  backgroundColor: colors.activeIcon,
};

const navLabel = {
  flexGrow: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Red counter on the right of an item (open reports, unread notifications).
const navCount = {
  ...badges.count,
  minWidth: 20,
  height: 20,
  px: 0.75,
  py: 0,
  justifyContent: 'center',
  boxSizing: 'border-box',
  fontSize: 11,
};

// Menu / popover paper of MUI components rendered in a portal.
const menuPaper = {
  backgroundColor: colors.surfaceRaised,
  border: `1px solid ${colors.border}`,
  borderRadius: '12px',
  color: colors.text,
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.6)',
};

const menuItem = {
  fontFamily: fonts.text,
  fontSize: 13,
  fontWeight: 500,
  minHeight: 36,
  borderRadius: radius.button,
  mx: 0.75,
  gap: 1.25,
  '&:hover': {
    backgroundColor: colors.hoverItem,
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: colors.activeItem,
  },
};

// Filter bar on top of a list (videos, users, reports): columns are set by each screen.
const filterBar = {
  display: 'grid',
  gap: 1.5,
  alignItems: 'center',
  p: 2,
  backgroundColor: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.card,
};

// Table head and rows: gridTemplateColumns and minWidth are set by each screen.
const tableHead = {
  ...label,
  display: 'grid',
  gap: 2,
  alignItems: 'center',
  px: 2.5,
  py: 1.5,
  borderBottom: `1px solid ${colors.border}`,
};

const tableRow = {
  display: 'grid',
  gap: 2,
  alignItems: 'center',
  px: 2.5,
  py: 1.25,
  borderBottom: `1px solid ${colors.borderSoft}`,
  fontFamily: fonts.text,
  fontSize: 13,
  color: colors.text,
  '&:hover': {
    backgroundColor: colors.rowHover,
  },
};

const emptyState = {
  py: 8,
  textAlign: 'center',
  fontFamily: fonts.text,
  fontSize: 14,
  color: colors.textSecondary,
};

// Thin scrollbar of the blocks that scroll inside a card or a popup.
const thinScrollbar = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${colors.scrollbar} transparent`,
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: colors.scrollbar,
    borderRadius: '2px',
  },
};

const snackbarOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
} as SnackbarOrigin;

// Auth pages (login, sign up, forgot password): same dark card as the connected space.
const authCard = {
  backgroundColor: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.card,
  boxShadow: 'none',
  color: colors.text,
};

// Inner padding of the auth cards: same space on the left and on the right.
const authBody = {
  px: 3,
  py: 2.5,
};

// Small texts of the auth pages: "Mot de passe oublié ?", "Tu n'as pas de compte ?", CGU.
const authSmallText = {
  fontFamily: fonts.text,
  fontSize: 13,
  color: colors.textSecondary,
};

// Hint or error under an input: slightly indented to belong to the input above.
const inputHint = {
  pl: '1%',
  mt: 0.75,
  fontFamily: fonts.text,
  fontSize: 12.5,
  lineHeight: 1.5,
};

// h4 (34px) from sm, smaller on a phone: "Mot de passe oublié ?" holds on one line.
const authTitle = {
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: { xs: 27, sm: '2.125rem' },
  letterSpacing: '0.5px',
  color: colors.text,
};

const authText = {
  fontFamily: fonts.text,
  color: colors.textSecondary,
};

// Links of the auth pages ("Mot de passe oublié ?", "Inscris-toi", CGU): underlined so they read as actions.
const authLink = {
  color: colors.text,
  fontWeight: 700,
  textDecoration: 'underline',
  textDecorationColor: colors.borderFocus,
  textUnderlineOffset: '3px',
};

const authButton = {
  ...buttonPrimary,
  ...buttonBlock,
};

const authSocialButton = {
  ...button,
  ...buttonBlock,
  gap: 1,
};

export {
  authBody,
  authButton,
  authCard,
  authLink,
  authSmallText,
  authSocialButton,
  authText,
  authTitle,
  badges,
  button,
  buttonBlock,
  buttonDanger,
  buttonIcon,
  buttonPrimary,
  card,
  chip,
  colors,
  emptyState,
  fadeIn,
  filterBar,
  filterInput,
  fonts,
  input,
  inputHint,
  label,
  link,
  mainHeight,
  menuItem,
  menuPaper,
  navCount,
  navIcon,
  navIconActive,
  navIndicator,
  navItem,
  navItemActive,
  navItemStep,
  navLabel,
  notices,
  pageTitle,
  radius,
  sizes,
  snackbarOrigin,
  tab,
  tabActive,
  tableHead,
  tableRow,
  thinScrollbar,
  tile,
  tileKey,
  tileValue,
};
